import {
  AiWorkspaceService,
  IAIWorkspaceRequest,
  useAddCreatorToFitRequest,
  useCreateCreatorFitRequest,
} from "@entities/aiWorkspace";
import { AI_WORKSPACE_QUERY_KEYS } from "@entities/aiWorkspace/lib/constants";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import { SocialMedia, SocialMediaSearch } from "@shared/lib/types/influencer";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { IParsedCreatorRow } from "./UploadSheetModal";

type CreatorFitCheck = NonNullable<IAIWorkspaceRequest["creatorFitChecks"]>[number];

export function useCreatorFitActions({
  workspaceId,
  activeId,
  promptHtml,
  promptChanged,
  checks,
  onOpenRequest,
  onPromptSaved,
}: {
  workspaceId: string;
  activeId: string;
  promptHtml: string;
  promptChanged: boolean;
  checks: CreatorFitCheck[];
  onOpenRequest?: (id: string, type: string) => void;
  onPromptSaved: (prompt: string) => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isRerunningAll, setIsRerunningAll] = useState(false);
  const queryClient = useQueryClient();

  const { create: createRequest, isCreating } = useCreateCreatorFitRequest(
    (res: BackednResponse<IAIWorkspaceRequest>) => {
      setSheetOpen(false);
      const newId = res?.data?._id;
      if (newId) onOpenRequest?.(newId, "creator-fit");
    }
  );
  const { addCreator, isAddingCreator } = useAddCreatorToFitRequest(() => {
    setSheetOpen(false);
  });

  const addCreators = (
    creatorOrCreators:
      | (Partial<SocialMedia> & Partial<SocialMediaSearch>)
      | IParsedCreatorRow[]
  ) => {
    const creatorsList = Array.isArray(creatorOrCreators)
      ? creatorOrCreators
      : [creatorOrCreators];
    const validCreators = creatorsList
      .filter((creator) => Boolean(creator.username && creator.platform))
      .map((creator) => ({
        username: creator.username as string,
        platform: creator.platform as string,
      }));
    if (!validCreators.length) {
      ToastService.error("Username or platform is missing.");
      return;
    }
    const fresh = validCreators.filter(
      (creator) =>
        !checks.some(
          (check) =>
            check.socialMedia?.username?.toLowerCase() ===
              creator.username.toLowerCase() &&
            check.socialMedia?.platform?.toLowerCase() ===
              creator.platform.toLowerCase()
        )
    );
    if (!fresh.length) {
      ToastService.error(
        validCreators.length === 1
          ? "Creator is already in this check"
          : "All creators are already in this check"
      );
      return;
    }
    if (activeId) {
      addCreator({ aiWorkspaceRequestId: activeId, creators: fresh });
      return;
    }
    createRequest({
      aiWorkspaceId: workspaceId,
      masterPrompt: promptHtml,
      creators: fresh,
    });
  };

  const rerunAll = async () => {
    if (!activeId || !checks.length) return;
    setIsRerunningAll(true);
    try {
      if (promptChanged) {
        await AiWorkspaceService.updateVetRequestPrompt({
          aiWorkspaceRequestId: activeId,
          prompt: promptHtml,
        });
        onPromptSaved(promptHtml);
      }
      await Promise.all(
        checks.map((check) =>
          AiWorkspaceService.reanalyzeCreatorFitCheck({
            aiWorkspaceRequestId: activeId,
            creatorFitCheckId: check._id,
          })
        )
      );
      await queryClient.invalidateQueries({
        queryKey: [AI_WORKSPACE_QUERY_KEYS.REQUEST, activeId],
      });
      ToastService.success("Creator Fit checks queued for re-analysis");
    } catch {
      ToastService.error("Failed to re-analyze Creator Fit checks");
    } finally {
      setIsRerunningAll(false);
    }
  };

  return {
    sheetOpen,
    setSheetOpen,
    isCreating,
    isAddingCreator,
    isRerunningAll,
    addCreators,
    rerunAll,
  };
}
